import React from 'react';
import { Repossession } from '@/types/common';

interface RepoDetailContentProps {
  repoCase: Repossession;
}

const getSeverityBadge = (severity: string) => {
  const baseClasses =
    'inline-flex items-center gap-2 rounded-lg border px-2 py-1 text-xs font-medium text-gray-700';

  return `${baseClasses} border-gray-200`;
};

const getSeverityDot = (severity: string) => {
  switch (severity) {
    case 'LOW':
      return 'bg-green-500';
    case 'MEDIUM':
      return 'bg-blue-500';
    case 'HIGH':
      return 'bg-orange-500';
    case 'CRITICAL':
      return 'bg-red-500';
    default:
      return 'bg-gray-400';
  }
};

const displayValue = (value?: string | number | null) => {
  if (value === null || value === undefined || value === '') return '-';
  return value;
};

const RepoDetailContent: React.FC<RepoDetailContentProps> = ({ repoCase }) => {
  return (
    <div className="w-full space-y-6 lg:flex-1">
      {/* Vehicle (match screenshot: simple card with icon + 3 rows) */}
      <div className="border border-gray-200 px-5 py-4 shadow-sm rounded-lg">
        <div className="mb-3 flex items-center gap-2 text-[16px] font-medium text-gray-800">
          <span className="ml-[-5px] inline-flex h-7 w-7 items-center justify-center text-gray-600">
            <i className="ri-motorbike-line text-base" />
          </span>
          Vehicle
        </div>

        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2">
            <div className="text-gray-500">Plate Number</div>
            <div className="text-right font-medium text-gray-900">
              {displayValue(repoCase.platNomor)}
            </div>
          </div>
          <div className="grid grid-cols-2">
            <div className="text-gray-500">OEM</div>
            <div className="text-right font-medium text-gray-900">
              {displayValue(repoCase.oemName)}
            </div>
          </div>
          <div className="grid grid-cols-2">
            <div className="text-gray-500">Models</div>
            <div className="text-right font-medium text-gray-900">
              {displayValue(repoCase.modelName)}
            </div>
          </div>
        </div>
      </div>

      {/* Mitra */}
      <div className="border border-gray-200 px-5 py-4 shadow-sm rounded-lg">
        <div className="mb-3 flex items-center gap-2 text-[16px] font-medium text-gray-800">
          <span className="ml-[-5px] inline-flex h-7 w-7 items-center justify-center text-gray-600">
            <i className="ri-user-line text-base" />
          </span>
          Mitra
        </div>

        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2">
            <div className="text-gray-500">MTR Code</div>
            <div className="text-right font-medium text-gray-900">
              {displayValue(repoCase.driverCode)}
            </div>
          </div>
          <div className="grid grid-cols-2">
            <div className="text-gray-500">Name</div>
            <div className="text-right font-medium text-gray-900">
              {displayValue(repoCase.driverName)}
            </div>
          </div>
          <div className="grid grid-cols-2">
            <div className="text-gray-500">Client</div>
            <div className="text-right font-medium text-gray-900">
              {displayValue(repoCase.clientName)}
            </div>
          </div>
          <div className="grid grid-cols-2">
            <div className="text-gray-500">Pitstop</div>
            <div className="text-right font-medium text-gray-900">
              {displayValue(repoCase.hubName)}
            </div>
          </div>
        </div>
      </div>

      {/* Repo Details (match screenshot highlight: only severity + assigned team) */}
      <div className="border border-gray-200 px-5 py-4 shadow-sm rounded-lg">
        <div className="mb-3 flex items-center gap-2 text-[16px] font-medium text-gray-800">
          <span className="ml-[-5px] inline-flex h-7 w-7 items-center justify-center text-gray-600">
            <i className="ri-alert-line text-base" />
          </span>
          Repo Details
        </div>

        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 items-center">
            <div className="text-gray-500">Severity</div>
            <div className="text-right">
              <span
                className={getSeverityBadge(repoCase.severity || 'UNKNOWN')}
              >
                <span
                  className={`h-2 w-2 rounded-full ${getSeverityDot(
                    repoCase.severity || 'UNKNOWN'
                  )}`}
                />
                {displayValue(repoCase.severity)}
              </span>
            </div>
          </div>
          <div className="grid grid-cols-2">
            <div className="text-gray-500">Assigned Repo Team</div>
            <div className="text-right font-medium text-gray-900">
              {displayValue(repoCase.assignedOpsTeam)}
            </div>
          </div>
        </div>
      </div>

      {/* Reason */}
      <div>
        <div className="mb-2 text-[14px] font-medium text-gray-500">Reason</div>
        <div className="border border-gray-200 p-4 shadow-sm rounded-lg">
          <p className="text-sm break-words leading-relaxed text-gray-700">
            {displayValue(repoCase.reason)}
          </p>
        </div>
      </div>

      {/* Notes */}
      <div>
        <div className="mb-2 text-[14px] font-medium text-gray-500 rounded-lg">
          Notes
        </div>
        <div className="border border-gray-200 p-4 shadow-sm rounded-lg">
          <p className="text-sm break-words leading-relaxed text-gray-700">
            {displayValue(repoCase.notes)}
          </p>
        </div>
      </div>
    </div>
  );
};

export default RepoDetailContent;
